import { Send, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Disclosure } from '@/components/disclosure';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Item, ItemContent, ItemDescription, ItemGroup, ItemTitle } from '@/components/ui/item';
import { Skeleton } from '@/components/ui/skeleton';
import { AI_UNAVAILABLE_TEXT, useAiStatus } from '@/features/ai/api/ai';
import { EvidenceList } from '@/features/ai/components/insight-footer';
import { toastApiError } from '@/lib/toast';
import {
  type AssistantReply,
  type AssistantScreen,
  useAsk,
  useAssistantHistory,
} from '../api/assistant';

const STARTERS = [
  'Como está minha operação hoje?',
  'Quanto vendi nos últimos 7 dias, por canal?',
  'O que está pendente?',
];

/**
 * Pergunte ao troig (ADR-063): a pessoa pergunta, o troig busca os números com as permissões dela
 * e a IA responde citando de onde vem cada um — nada é calculado pela IA.
 */
export function AssistantCard({
  compact = false,
  screen = null,
}: {
  /** No painel lateral: cabeçalho empilhado, sem a borda do cartão. */
  compact?: boolean;
  screen?: AssistantScreen | null;
} = {}) {
  const ai = useAiStatus();
  const history = useAssistantHistory();
  const ask = useAsk();
  const [question, setQuestion] = useState('');
  const ready = ai.data?.functions.assistant?.available === true;

  const send = (text: string) => {
    const value = text.trim();
    if (value.length < 3 || ask.isPending) return;
    ask.mutate(
      { question: value, screen },
      {
        onSuccess: () => setQuestion(''),
        onError: (error) => toastApiError(error, 'Não consegui responder agora.'),
      },
    );
  };

  const replies = history.data ?? [];
  const suggestions = replies.at(-1)?.followUps.length ? replies.at(-1)!.followUps : STARTERS;

  return (
    <Card className={compact ? 'border-0 shadow-none' : undefined}>
      <CardHeader
        className={
          compact ? 'flex flex-col gap-4' : 'flex flex-col gap-4 lg:flex-row lg:items-center'
        }
      >
        <div className={compact ? 'space-y-1' : 'space-y-1 lg:w-2/5'}>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="size-5" aria-hidden /> Pergunte ao troig
            <Badge variant="brand-soft">beta</Badge>
          </CardTitle>
          <CardDescription>
            O dia, vendas e resultado por canal, margem, custo e estoque de um produto, o pedido de
            compra do dia e o que está pendente. Os números vêm do troig; a IA só escreve a
            resposta.
          </CardDescription>
        </div>
        {ready ? (
          <form
            className="flex flex-1 gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              send(question);
            }}
          >
            <Input
              aria-label="Sua pergunta"
              placeholder="Ex.: quais anúncios estão me fazendo perder dinheiro?"
              value={question}
              maxLength={500}
              onChange={(event) => setQuestion(event.target.value)}
            />
            <Button type="submit" disabled={ask.isPending || question.trim().length < 3}>
              <Send aria-hidden /> {ask.isPending ? 'Pensando…' : 'Perguntar'}
            </Button>
          </form>
        ) : (
          <p className="flex-1 text-sm text-muted-foreground">
            {ai.data?.functions.assistant?.reason
              ? AI_UNAVAILABLE_TEXT[ai.data.functions.assistant.reason]
              : ai.isPending
                ? 'Conferindo a IA…'
                : 'A IA não está disponível agora.'}
          </p>
        )}
      </CardHeader>
      {ready && (
        <CardContent className="space-y-3">
          {replies.length > 0 && (
            <ItemGroup className="max-h-96 gap-3 overflow-y-auto">
              {replies.map((reply) => (
                <Exchange key={reply.id} reply={reply} />
              ))}
            </ItemGroup>
          )}
          {ask.isPending && <Skeleton className="h-16 w-full" />}
          <div className="flex flex-wrap gap-2">
            {suggestions.map((item) => (
              <Button
                key={item}
                variant="outline"
                size="sm"
                disabled={ask.isPending}
                onClick={() => send(item)}
              >
                {item}
              </Button>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function Exchange({ reply }: { reply: AssistantReply }) {
  return (
    <>
      <Item variant="muted" size="sm">
        <ItemContent>
          <ItemDescription>Você</ItemDescription>
          <ItemTitle className="line-clamp-none font-normal">{reply.question}</ItemTitle>
        </ItemContent>
      </Item>
      <Item variant="outline" size="sm">
        <ItemContent className="space-y-2">
          <ItemDescription>troig</ItemDescription>
          <p className="whitespace-pre-line text-sm">{reply.answer}</p>
          <EvidenceList facts={reply.evidence} ids={reply.evidence.map((fact) => fact.id)} />
          {reply.limitations.length > 0 && (
            <ul className="list-disc pl-4 text-xs text-muted-foreground">
              {reply.limitations.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
          {reply.facts.length > 0 && (
            <Disclosure size="xs" summary={`Todos os dados buscados (${reply.facts.length})`}>
              <dl className="grid gap-1 sm:grid-cols-label sm:gap-x-4">
                {reply.facts.map((fact) => (
                  <div key={fact.id} className="contents">
                    <dt className="text-muted-foreground">{fact.label}</dt>
                    <dd className="tabular">{fact.value}</dd>
                  </div>
                ))}
              </dl>
            </Disclosure>
          )}
        </ItemContent>
      </Item>
    </>
  );
}
